import React, { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import { useQuery } from "@apollo/client"

import PageHeader from "../../components/PageHeader"
import { SplitButton } from "primereact/splitbutton"
import { LoaderMedium } from "../../components/Loaders"
import { GROUP_SESSION_PARTICIPANT } from "./queries"
import { UserContext } from "../../context/userContext"
import { parseDate } from "../../helpers/dateNormalization"

import useLabels from "../../hooks/useLabels"

import GenericTable from "../../components/DataTable/GenericTable"
import { useFilters } from "../../components/Filters/useFilters"
import FilterHeader from "../../components/DataTable/FilterHeader"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import ParticipantGroupSidebar from "../../components/sidebars/ParticipantGroupSidebar"

import styles from "../../styles/Page-with-Sidebar.module.css"
import { navigateWithOrigin } from "../../helpers/navigation"

const ID_TEMPLATE = "group_session"
const ID_BLOCKS = [
  "session",
  "session__group_session",
  "session__group_session__participant_note",
]

const formatDate = (value) => {
  return value.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

export default function GroupSessionList() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])

  const [loadingLabels, labels] = useLabels(["group_session"])

  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  // Create helper functions....
  function handleOptsForFilter(data, key) {
    const optionList = _.uniq(data.map((item) => item[key]).flat())
      .filter((value) => value !== "")
      .map((label) => label)

    const optionListFlattened = optionList
      .map((item) => item.split(",").map((value) => value.trim()))
      .flat()

    const optionListUniqueValues = Array.from(new Set(optionListFlattened))

    const optionsWithStruct = optionListUniqueValues.map((option) => {
      return { label: option, value: option }
    })

    const optionsWithDefault = [
      { label: "All", value: "" },
      ...optionsWithStruct,
    ]

    return optionsWithDefault
  }

  useQuery(GROUP_SESSION_PARTICIPANT, {
    variables: {
      organization_id: userCtx.session.user.organization_id,
      idTemplate: ID_TEMPLATE,
      idBlocks: ID_BLOCKS,
      staff_id: userCtx.session.user.staff_id,
    },
    fetchPolicy: "no-cache",
    skip: !userCtx,
    onCompleted: (data) => {
      if (!data.groupSession) {
        setLoading(false)
        return
      }

      const dataFormatted = JSON.parse(data.groupSession.data)

      const formattedData = _.map(dataFormatted, (template) => {
        const blocks = _.groupBy(template, "block_name")
        const session = _.isEmpty(blocks?.session) ? {} : blocks?.session[0]
        const sessionGroup = _.isEmpty(blocks?.session__group_session)
          ? {}
          : blocks?.session__group_session[0]
        const sessionGroupNote = _.isEmpty(
          blocks?.session__group_session__participant_note,
        )
          ? {}
          : blocks?.session__group_session__participant_note[0]

        const handleLocations = () => {
          const locationsList = _.map(
            session.values?.session__location_ids,
            ({ label }) => label,
          )
          return _.join(locationsList, ", ")
        }

        const handleParticipants = () => {
          const listOfParticipants = _.map(
            sessionGroupNote?.values?.participant_notes_multiblock,
            ({ participant_note_list }) => {
              return participant_note_list[0]?.name
            },
          )
          return _.join(listOfParticipants, ", ")
        }

        const handleProgramIdentityIds = () => {
          const listOfPrograms = _.map(
            sessionGroup?.values?.session__program_identity_ids,
            (program_list) => {
              return program_list?.label
            },
          )
          return _.join(listOfPrograms, ", ")
        }

        return {
          date: parseDate(session?.values?.session_date),
          locations: handleLocations(),
          length: `${session?.values?.session_length} minutes`,
          topics: []
            .concat(sessionGroup?.values?.session_topic ?? [])
            .join(", "),
          channel: session?.values?.session_channel,
          submittedBy: session.staff_full_name,
          notes: sessionGroupNote?.values?.session_notes,
          participants: handleParticipants(),
          programs: handleProgramIdentityIds(),
          edit: (
            <SplitButton
              label="View"
              to={`/form/${ID_TEMPLATE}/1/${session.idStamp}`}
              onClick={() => {
                userCtx.changeParticipant(undefined)
                navigateWithOrigin(
                  navigate,
                  `/form/view/${ID_TEMPLATE}/${session.idStamp}`,
                )
              }}
              model={[
                {
                  label: "Edit",
                  command: () => {
                    navigate(`/form/${ID_TEMPLATE}/1/${session.idStamp}`)
                  },
                },
              ]}
            />
          ),
        }
      })

      setData(formattedData)
      setLoading(false)
    },
  })

  if (loading || loadingLabels) return <LoaderMedium />

  const uniqueLocations = handleOptsForFilter(data, "locations")
  const uniqueFacilitatingStaff = handleOptsForFilter(data, "submittedBy")
  const uniqueTopics = handleOptsForFilter(data, "topics")
  const uniqueProgramIDs = handleOptsForFilter(data, "programs")

  const filterConfig = [
    {
      label: "Location",
      value: filters.locations?.value || "",
      options: uniqueLocations,
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      enableFilter: true,
    },
    {
      label: "Facilitating Staff",
      value: filters.submittedBy?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("submittedBy", e.value),
      onClear: () => onFilterChange("submittedBy", null),
      enableFilter: true,
    },
    {
      label: "Topics",
      value: filters.topics?.value || "",
      options: uniqueTopics,
      onChange: (e) => onFilterChange("topics", e.value),
      onClear: () => onFilterChange("topics", null),
      enableFilter: true,
    },
    {
      label: "Program ID",
      value: filters.programs?.value || "",
      options: uniqueProgramIDs,
      onChange: (e) => onFilterChange("programs", e.value),
      onClear: () => onFilterChange("programs", null),
      enableFilter: true,
    },
  ]

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => formatDate(rowData.date),
    },
    { field: "locations", header: "Location", sortable: true },
    { field: "length", header: "Engagement Length", sortable: true },
    { field: "channel", header: "Engagement channel", sortable: true },
    { field: "submittedBy", header: "Facilitating staff", sortable: true },
    { field: "participants", header: "Participants", sortable: true },
    { field: "topics", header: "Topics", sortable: true },
    { field: "programs", header: "Program ID", sortable: true },
    { field: "edit", header: "Action" },
  ]

  return (
    <>
      <PageSidebarContent>
        <ParticipantGroupSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <div className={styles.content}>
          <PageHeader
            title={labels.group_session}
            actions={[
              {
                to: "/form/group_session/1",
                label: `New ${labels.group_session}`,
              },
            ]}
          />
          <GenericTable
            data={data}
            columns={columns}
            filters={filters}
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            header={
              <FilterHeader
                filters={filterConfig}
                globalFilterValue={globalFilterValue}
                onGlobalFilterChange={onGlobalFilterChange}
                clearGlobalFilter={clearGlobalFilter}
                clearAllFilters={clearAllFilters}
              />
            }
            paginator
            rows={50}
          />
        </div>
      </div>
    </>
  )
}
